import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import LoadingScreen from './components/LoadingScreen';
import SeoManager from './components/SeoManager';
import AnalyticsTracker from './components/AnalyticsTracker';

const HomePage = lazy(() => import('./pages/HomePage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const SystemsPage = lazy(() => import('./pages/SystemsPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const SpecialOffersPage = lazy(() => import('./pages/SpecialOffersPage'));
const AboutMore = lazy(() => import('./pages/AboutMore'));
const WhyUs = lazy(() => import('./pages/WhyUs'));
const DisclaimerPage = lazy(() => import('./pages/DisclaimerPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const PaymentPolicyPage = lazy(() => import('./pages/PaymentPolicyPage'));
const PlanPaymentPage = lazy(() => import('./pages/PlanPaymentPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const PaymentCancelPage = lazy(() => import('./pages/PaymentCancelPage'));
const CheckoutRedirect = lazy(() => import('./components/CheckoutRedirect'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const LearnMorePage = lazy(() => import('./pages/LearnMorePage'));
const PlanDetailsPage = lazy(() => import('./pages/PlanDetailsPage'));

function App() {
  const [cursorPos, setCursorPos] = useState({ x: -100, y: -100 });
  const [isLoading, setIsLoading] = useState(true);
  const whatsappNumber = '27832832639';
  const whatsappLink = `https://wa.me/${whatsappNumber}`;

  useEffect(() => {
    const handleMove = (e) => {
      setCursorPos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMove);
    return () => window.removeEventListener('mousemove', handleMove);
  }, []);

  useEffect(() => {
    const loadingTimer = setTimeout(() => {
      setIsLoading(false);
    }, 1800);

    return () => clearTimeout(loadingTimer);
  }, []);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Router>
      <ScrollToTop />
      <SeoManager />
      <AnalyticsTracker />
      <div className="min-h-screen flex flex-col">
        {/* Cursor follower */}
        <div
          className="pointer-events-none fixed z-[9999] transition-transform duration-75 ease-out"
          style={{
            transform: `translate(${cursorPos.x - 6}px, ${cursorPos.y - 6}px)`,
          }}
        >
          <div className="h-3 w-3 rounded-full bg-blue-500/80 shadow-[0_0_12px_rgba(59,130,246,0.6)]" />
        </div>

        <Navbar />
        <main className="flex-grow pt-24">
          <Suspense fallback={<LoadingScreen />}>
            <AnimatePresence mode="wait">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/systems" element={<SystemsPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/special-offers" element={<SpecialOffersPage />} />
                <Route path="/plans/:plan" element={<PlanDetailsPage />} />
                <Route path="/about-more" element={<AboutMore />} />
                <Route path="/why-us" element={<WhyUs />} />
                <Route path="/disclaimer" element={<DisclaimerPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/payment-policy" element={<PaymentPolicyPage />} />
                <Route path="/payment" element={<PlanPaymentPage />} />
                <Route path="/payment/success" element={<PaymentSuccessPage />} />
                <Route path="/payment/cancel" element={<PaymentCancelPage />} />
                <Route path="/checkout" element={<CheckoutRedirect />} />
                <Route path="/documents" element={<DocumentsPage />} />
                <Route path="/learn-more" element={<LearnMorePage />} />
              </Routes>
            </AnimatePresence>
          </Suspense>
        </main>
        {/* WhatsApp Floating Button (global) */}
        <a
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          className="fixed left-6 bottom-6 z-50 flex items-center justify-center w-14 h-14 bg-blue-500 rounded-full shadow-lg hover:bg-green-500 transition-colors"
          title="Chat with us on WhatsApp"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 32 32"
            width="32"
            height="32"
            fill="white"
          >
            <path d="M16 3C9.373 3 4 8.373 4 15c0 2.385.832 4.584 2.236 6.393L4 29l7.824-2.205C13.41 27.597 14.686 28 16 28c6.627 0 12-5.373 12-12S22.627 3 16 3zm0 22c-1.13 0-2.238-.188-3.287-.558l-.235-.08-4.646 1.31 1.324-4.522-.15-.232C7.01 18.13 6 16.627 6 15c0-5.514 4.486-10 10-10s10 4.486 10 10-4.486 10-10 10zm5.29-7.71c-.26-.13-1.54-.76-1.78-.85-.24-.09-.41-.13-.58.13-.17.26-.67.85-.82 1.02-.15.17-.3.19-.56.06-.26-.13-1.09-.4-2.07-1.28-.76-.68-1.27-1.52-1.42-1.78-.15-.26-.02-.4.11-.53.11-.11.26-.3.39-.45.13-.15.17-.26.26-.43.09-.17.04-.32-.02-.45-.06-.13-.58-1.39-.8-1.91-.21-.51-.43-.44-.58-.45-.15-.01-.32-.01-.5-.01-.17 0-.45.06-.68.28-.23.22-.9.88-.9 2.15s.92 2.49 1.05 2.66c.13.17 1.81 2.77 4.39 3.77.61.21 1.09.33 1.46.42.61.15 1.16.13 1.6.08.49-.06 1.54-.63 1.76-1.24.22-.61.22-1.13.15-1.24-.07-.11-.24-.17-.5-.3z" />
          </svg>
        </a>
        <Footer />
      </div>
    </Router>
  );
}

export default App; 